// Finance queues widget — the supps lifecycle + commissions at a glance
// (Ship 2, Aug 25 2026). Head-counts only; acting happens on the finance board.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

interface Counts {
  ready: number;
  updated: number;
  atCarrier: number;
  finalInvoice: number;
  commPending: number;
  commApproved: number;
}

export default function QueuesWidget() {
  const [c, setC] = useState<Counts | null>(null);

  useEffect(() => {
    let cancelled = false;
    const stageCount = (stage: string) =>
      supabase.from("deals").select("id", { count: "exact", head: true }).eq("stage", stage);
    (async () => {
      const [r1, r2, r3, r4, cp, ca] = await Promise.all([
        stageCount("ready_for_supps"),
        stageCount("ready_for_updated_invoice"),
        stageCount("supps_waiting_on_claims"),
        stageCount("supps_waiting_on_final_invoice"),
        supabase
          .from("deals")
          .select("id", { count: "exact", head: true })
          .not("commission_ready_date", "is", null)
          .eq("commission_approved", false)
          .eq("commission_paid", false),
        supabase
          .from("deals")
          .select("id", { count: "exact", head: true })
          .eq("commission_approved", true)
          .eq("commission_paid", false),
      ]);
      if (cancelled) return;
      setC({
        ready: r1.count ?? 0,
        updated: r2.count ?? 0,
        atCarrier: r3.count ?? 0,
        finalInvoice: r4.count ?? 0,
        commPending: cp.count ?? 0,
        commApproved: ca.count ?? 0,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const row = (label: string, sub: string, n: number | undefined, to: string, key: string) => (
    <Link
      to={to}
      onClick={() => logCardClick(key)}
      className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-b-0"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] font-bold text-bardin-ink">{label}</div>
        <div className="text-[10.5px] text-slate-400">{sub}</div>
      </div>
      <span
        className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold ${
          (n ?? 0) > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-400"
        }`}
      >
        {n ?? "…"}
      </span>
    </Link>
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🧾</span>
        <span className="font-bold text-sm text-bardin-ink">Queues</span>
      </div>
      {row("Final invoice due", "supps approved, invoice next", c?.finalInvoice, "/finance?supp=supps_waiting_on_final_invoice", "widget_queues_final")}
      {row("Supps at carrier", "waiting on claims", c?.atCarrier, "/finance?supp=supps_waiting_on_claims", "widget_queues_carrier")}
      {row("Ready for supps", "fresh from build", c?.ready, "/finance?supp=ready_for_supps", "widget_queues_ready")}
      {row("Updated invoice", "ready to send", c?.updated, "/finance?supp=ready_for_updated_invoice", "widget_queues_updated")}
      {row("Commissions · to approve", "Chad's queue", c?.commPending, "/finance?tab=commissions", "widget_queues_comm_pending")}
      {row("Commissions · to pay", "approved, awaiting payout", c?.commApproved, "/finance?tab=commissions", "widget_queues_comm_pay")}
    </div>
  );
}
